import { ReactElement } from "react";
import Head from "next/head";
import { MainLayout } from "@/containers/layout/NowLayout";
import RentHome from "@/components/landing-new/RentToOwn";
import WhoCanJoin from "@/components/landing-new/howItWorks/whoCanJoin";
import ContactBanner from "@/components/landing-new/howItWorks/ContactBanner";
import { generateCarouselData } from "@/components/howitworks/utils/utils";
import CarouselItem from "@/components/howitworks/components/CarouselItem";
import { Props } from "@/components/howitworks/components/types";
import Link from "next/link";
import { Element, scroller } from "react-scroll";
import { useState } from "react";
import Step1 from "@/components/landing-new/howItWorks/Step1";
import Step2 from "@/components/landing-new/howItWorks/Step2";
import Step3 from "@/components/landing-new/howItWorks/Step3";
import Step4 from "@/components/landing-new/howItWorks/Step4";
import Step5 from "@/components/landing-new/howItWorks/Step5";
import BudgetCalculator from "@/components/landing-new/BudgetCalculator";
import routes from "@/constants/routes";
import getConfig from "next/config";
import HowItWorksV2 from "@/components/howitworks-v2/HowItWorksV2";

const { publicRuntimeConfig } = getConfig();

const HowItWorksPage = ({ theme = "dark" }: Props): JSX.Element => {
  const carouselData = generateCarouselData() || [];
  const [step1OpenFaq, setStep1OpenFaq] = useState<number | undefined>(undefined);
  const isRefreshEnabled = publicRuntimeConfig.NEXT_ENABLE_HOW_IT_WORKS_REFRESH === "1";

  const handleHowIsPossible = () => {
    setStep1OpenFaq(2);

    scroller.scrollTo("step1-faq-2", { smooth: true, duration: 50, offset: -80 });
  };

  return (
    <>
      <Head>
        <title>How Keyzy Works: Five simple steps to home ownership with Keyzy</title>
        <link rel="canonical" href="https://www.keyzy.com/how-it-works" />
        <meta
          name="description"
          key="description"
          content="Discover how Keyzy's rent-to-own process works in five simple steps, helping you transition from renting to home ownership."
        />
        <meta
          name="twitter:card"
          content="Discover how Keyzy's rent-to-own process works in five simple steps, helping you transition from renting to home ownership."
          key="twcard"
        />
        <meta name="twitter:creator" content="keyzyhomes" key="twhandle" />
        <meta property="og:url" content="/" key="ogurl" />
        <meta
          property="og:image"
          content="/how-it-works/keyzy-meta-how-it-works.png"
          key="ogimage"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Keyzy" key="ogsitename" />
        <meta
          property="og:title"
          content="How Keyzy Works: Five simple steps to home ownership with Keyzy"
          key="ogtitle"
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <meta
          property="og:description"
          content="Discover how Keyzy's rent-to-own process works in five simple steps, helping you transition from renting to home ownership."
          key="ogdesc"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              name: "Keyzy How It Works",
              description:
                "Discover how Keyzy's rent-to-own process works in five simple steps, helping you transition from renting to home ownership.",
              provider: {
                "@type": "Organisation",
                name: "Keyzy",
                url: "https://keyzy.com",
              },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://keyzy.com/" },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "How It Works",
                  item: "https://keyzy.com/how-it-works",
                },
              ],
            }),
          }}
        />
      </Head>

      {isRefreshEnabled ? (
        <HowItWorksV2 />
      ) : (
      <div className="bg-white">
        <RentHome />

        <Element name="calculator">
          <BudgetCalculator onHowIsPossible={handleHowIsPossible} />
        </Element>

        <div>
          <div
            className="bg-white sticky top-16 z-40 flex lg:hidden justify-around w-full md:justify-around h-max min-h-[60px] py-2
            after:content-[''] after:absolute after:bottom-9 after:left-0 after:h-px after:w-full after:cursor-auto after:border after:border-landing-blue
            truncate"
          >
            {carouselData.map(({ header }, idx) => (
              <CarouselItem
                key={idx}
                title={header}
                index={idx}
                isLastItem={idx === carouselData.length - 1}
                theme={theme}
                scrollOffset={-70}
              />
            ))}
          </div>

          <div className="relative flex flex-col lg:flex-row">
            <div>
              <Element id="1" name="1">
                <Step1 openFaqIndex={step1OpenFaq} setOpenFaqIndex={setStep1OpenFaq} />
              </Element>
              <Element id="2" name="2">
                <Step2 />
              </Element>
              <Element id="3" name="3">
                <Step3 />
              </Element>
              <Element id="4" name="4">
                <Step4 />
              </Element>
              <Element id="5" name="5">
                <Step5 />
              </Element>
            </div>
            <div className="hidden lg:flex bg-white">
              <div
                className={`sticky flex justify-around w-full lg:justify-around lg:flex-col h-max lg:max-w-max min-h-[400px] z-40 top-48 lg:my-48 mr-10 py-4
              after:content-[''] after:absolute after:-bottom-0 after:left-7 after:h-full after:w-px lg:after:w-px after:cursor-auto after:border after:border-landing-blue `}
              >
                {carouselData.map(({ header }, idx) => (
                  <CarouselItem
                    key={idx}
                    title={header}
                    index={idx}
                    isLastItem={idx === carouselData.length - 1}
                    theme={theme}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-40 flex py-4 lg:py-6 px-5 lg:pl-0 lg:pr-[100px] text-2xl font-bold text-white bg-landing-blue">
          <div className="flex-1 text-base font-normal text-right">
            <span className="hidden lg:inline text-2xl font-bold">
              Like what you see? Get started with Keyzy
            </span>
            <Link href={routes.register}>
              <a className="inline-block text-center align-middle text-base font-bold text-white rounded-md bg-landing-cherry lg:w-[230px] h-[42px] lg:ml-4 pt-2 w-full">
                <span className="hidden lg:inline font-base font-bold">Sign up</span>
                <span className="lg:hidden">Get started with Keyzy</span>
              </a>
            </Link>
          </div>
        </div>

        <WhoCanJoin />
        <ContactBanner />
      </div>
      )}
    </>
  );
};

HowItWorksPage.getLayout = function getLayout(page: ReactElement) {
  return <MainLayout>{page}</MainLayout>;
};

export default HowItWorksPage;
